import { useState, type FormEvent } from "react";
import { Download, CheckCircle, FileText, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trackEvent, trackFormSubmission } from "@/lib/analytics";

interface LeadMagnetProps {
  className?: string;
  defaultIndustry?: string;
}

const industriesList = [
  "SaaS & Technology",
  "E-commerce & DTC",
  "Startups (Pre-seed / Seed)",
  "Nonprofits & Research",
  "Professional Services & Agencies",
  "India→US Cross-Border",
  "Healthcare",
  "Other",
];

export function LeadMagnet({ className = "", defaultIndustry = "" }: LeadMagnetProps) {
  const [email, setEmail] = useState("");
  const [industry, setIndustry] = useState(defaultIndustry);
  const [website, setWebsite] = useState(""); // Honeypot field
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      toast.error("Please enter a valid work email address.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, industry, website }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to submit. Please try again.");
      }

      setDownloaded(true);
      toast.success("Checklist ready! Your download has started.");

      trackFormSubmission("lead_magnet_checklist", {
        industry: industry || "Not specified",
      });
      trackEvent("pdf_download", {
        pdfName: "monthly-close-checklist.pdf",
        source: "inline_lead_magnet",
      });

      // Trigger download
      const link = document.createElement("a");
      link.href = data.downloadUrl || "/assets/monthly-close-checklist.pdf";
      link.download = "Finendra-Monthly-Close-Checklist-Founders.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Something went wrong.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border border-brand-navy/10 bg-brand-surface p-8 md:p-12 shadow-sm ${className}`}
    >
      <div className="mx-auto max-w-4xl">
        <div className="grid items-center gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-navy">
              <FileText className="size-3.5 text-brand-accent" />
              Free Founder Resource
            </div>
            <h3 className="mt-4 font-display text-2xl font-bold text-brand-navy sm:text-3xl">
              The 27-Point Founder Month-End Close Checklist
            </h3>
            <p className="mt-3 text-base leading-relaxed text-brand-navy/70">
              The step-by-step operating checklist used by seed-stage startups and VC-backed
              companies to close their books by the 5th of every month. Reconcile Stripe, clear AP,
              track deferred revenue, and calculate true cash runway.
            </p>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-xs font-medium text-brand-navy/80">
              <li className="flex items-center gap-1.5">
                <CheckCircle className="size-3.5 text-brand-accent shrink-0" />
                ASC 606 Rev Rec Guidelines
              </li>
              <li className="flex items-center gap-1.5">
                <CheckCircle className="size-3.5 text-brand-accent shrink-0" />
                Runway & Burn Formulas
              </li>
              <li className="flex items-center gap-1.5">
                <CheckCircle className="size-3.5 text-brand-accent shrink-0" />
                Multi-State Payroll Steps
              </li>
              <li className="flex items-center gap-1.5">
                <CheckCircle className="size-3.5 text-brand-accent shrink-0" />
                Audit-Ready Workpapers
              </li>
            </ul>
          </div>

          <div className="lg:col-span-5">
            {downloaded ? (
              <div className="rounded-2xl border border-brand-accent/30 bg-background p-6 text-center shadow-md">
                <CheckCircle className="mx-auto size-12 text-brand-accent" />
                <h4 className="mt-3 font-display text-xl font-bold text-brand-navy">
                  Download Started!
                </h4>
                <p className="mt-2 text-sm text-brand-navy/60">
                  Your PDF is downloading. Check your downloads folder or click below to
                  re-download.
                </p>
                <a
                  href="/assets/monthly-close-checklist.pdf"
                  download="Finendra-Monthly-Close-Checklist-Founders.pdf"
                  className="mt-5 inline-flex items-center gap-2 rounded-xl bg-brand-navy px-5 py-2.5 text-sm font-semibold text-background hover:bg-brand-accent"
                >
                  <Download className="size-4" /> Download Again
                </a>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="rounded-2xl border border-brand-navy/10 bg-background p-6 shadow-md"
              >
                <h4 className="font-display text-lg font-bold text-brand-navy">
                  Get Instant Free Access
                </h4>
                <p className="mt-1 text-xs text-brand-navy/60">
                  Delivered directly to your screen as a printable 2-page PDF.
                </p>

                {/* Honeypot field - visually hidden */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  className="absolute -left-[9999px] size-0 opacity-0"
                  aria-hidden="true"
                />

                <div className="mt-4 space-y-3">
                  <div>
                    <label htmlFor="lead-email" className="sr-only">
                      Work Email
                    </label>
                    <input
                      id="lead-email"
                      type="email"
                      required
                      placeholder="founder@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-xl border border-brand-navy/15 bg-background px-4 py-3 text-sm text-brand-navy placeholder:text-brand-navy/40 focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    />
                  </div>

                  <div>
                    <label htmlFor="lead-industry" className="sr-only">
                      Industry
                    </label>
                    <select
                      id="lead-industry"
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      className="w-full rounded-xl border border-brand-navy/15 bg-background px-4 py-3 text-sm text-brand-navy focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    >
                      <option value="">Select your industry (optional)</option>
                      {industriesList.map((ind) => (
                        <option key={ind} value={ind}>
                          {ind}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-navy py-3 text-sm font-bold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-brand-accent hover:shadow-lg active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="size-4 animate-spin" /> Preparing PDF...
                      </>
                    ) : (
                      <>
                        Download Free PDF <ArrowRight className="size-4" />
                      </>
                    )}
                  </button>
                </div>

                <p className="mt-3 text-center text-[0.7rem] text-brand-navy/50">
                  No spam ever. Unsubscribe anytime in 1-click.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
